import React from 'react';
import {Row, Col} from 'reactstrap';
import {NavLink} from 'react-router-dom';
import TierBadge from '../TierBadge';
import OrderStatusBadge from '../OrderStatusBadge';
import OrganizationPatch from '../OrganizationPatch';
import {imgUrl} from '../../utils/api-request';
import './OrderCard.scss'
import {isAdmin} from '../../utils/admin';

/**
 * A row in the orders list.
 *
 * In Past Orders the row is a *purchase*, not an order — the same document is
 * bought over and over, so the server sends one row per purchase with that
 * purchase attached. The card then shows what that particular booking cost and
 * when it screened, rather than whatever the document happens to hold now.
 *
 * Elsewhere (Current, Not Ordered Yet) there is no `purchase` and the card
 * falls back to the order's own fields.
 */
const OrderCard = ({ order, purchase }) => {
    if (order && !order.version) {
        console.error(`The order ObjectId('${order._id}') has no valid version related.`, order);
        return false;
    }

    // Once a hire period ends, bac-python unsets price, downpayment,
    // screeningDate, purchasedAt and purchasedBy and recycles the order back
    // into the catalogue, so a past row cannot read them off the document.
    const price = purchase ? purchase.price : (order.price ?? order.downpayment);
    const screeningDate = purchase ? order.historyDate : order.screeningDate;
    const purchasedAt = purchase ? purchase.purchasedAt : order.purchasedAt;
    const purchasedBy = purchase ? purchase.purchasedBy : order.purchasedBy;

    const displayPrice = price != null ? `${price} credits` : 'N/A';

    return (
        // A past row is one purchase among several, so it links to that
        // purchase rather than dropping the user on the latest one.
        <NavLink
            to={purchase ? `/orders/${order._id}?purchase=${purchase.index}` : `/orders/${order._id}`}
            className="text-decoration-none text-white"
        >
            <Row className="order-card-component py-3 mx-0 my-3 align-items-center">
                {/* Poster Column */}
                <Col xs="auto">
                    {order.movie.poster ? (
                        <img
                            className="logo-img"
                            width={100}
                            alt={order.movie.title}
                            src={imgUrl(order.movie?.poster?.imageUrl)}
                        />
                    ) : (
                        <div className="logo-img-placeholder d-flex align-items-center justify-content-center">
                            <span className="movie-title-text text-center px-1">{order.movie.title}</span>
                        </div>
                    )}
                </Col>

                {/* Content Column */}
                <Col className="position-relative">
                    {/* Section 1: Title Row with Status Badge */}
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="title text-capitalize text-primary">
                            {order.movie.title}
                        </div>
                        <OrderStatusBadge status={order.statusWorkflow} isCard />
                    </div>

                    {/* Section 2: Version Info */}
                    <div className="text-white-50 small mb-3">
                        <span className="fas fa-fw fa-clapperboard me-2 text-primary"/>
                        {order.version?.name}
                    </div>

                    {/* Section 3: Metadata Row (Tier, Price, Date) */}
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                        {/* Tier Badge */}
                        {isAdmin && (
                            <TierBadge tier={order.organization.tier}/>
                        )}

                        {/* Price */}
                        <div className="order-credits d-inline-flex align-items-center small">
                            <span className="fas fa-fw fa-coins me-2 text-primary"/>
                            {displayPrice}
                        </div>

                        {/* Screening Date */}
                        {screeningDate && (
                            <div className="order-credits d-inline-flex align-items-center small">
                                <span className="fas fa-fw fa-calendar-days me-2 text-primary" />
                                Screening Date: {new Date(screeningDate).toLocaleDateString()}
                            </div>
                        )}
                    </div>

                    {/* Section 4: Purchase Attribution */}
                    {purchasedAt && (
                        <div className="user small text-white-50">
                            <span className="fas fa-user mx-2" />
                            {purchasedBy && (
                                <>
                                    {purchasedBy.firstName} {purchasedBy.lastName}
                                    <span className="mx-2">•</span>
                                </>
                            )}
                            {new Date(purchasedAt).toLocaleString()}
                        </div>
                    )}

                    {/* Organization Patch - Bottom Right */}
                    {isAdmin && (
                        <div className="position-absolute bottom-0 end-0 mb-2 me-2">
                            <OrganizationPatch organization={order.organization} />
                        </div>
                    )}
                </Col>
            </Row>
        </NavLink>
    );
};

export default OrderCard;
